import {Directive, Input, OnDestroy, TemplateRef, ViewContainerRef} from '@angular/core';
import {Subscription} from 'rxjs';
import {RoleName, RoleService} from '../services/role.service';

/**
 * Показывает элемент, если у пользователя есть роль.
 *
 *  <button *hasRole="'BDAdmin'">Сохранить</button>
 *  <button *hasRole="['BDAdmin', 'MioAdministrator']">Сохранить</button>          // хотя бы одна роль
 *  <button *hasRole="['BDAdmin', 'MioUser']; mode: 'every'">Сохранить</button>    // все роли сразу
 *  <button *hasRole="'BDAdmin'; else readOnly">Сохранить</button>
 *  <ng-template #readOnly>...</ng-template>
 */
@Directive({
  selector: '[hasRole]',
  standalone: true
})
export class HasRoleDirective implements OnDestroy {

  private roles: RoleName[] = [];
  private mode: 'any' | 'every' = 'any';
  private elseTemplate: TemplateRef<any> | null = null;
  private renderedTemplate: TemplateRef<any> | null | undefined = undefined;
  private sub: Subscription;

  constructor(private templateRef: TemplateRef<any>,
              private viewContainer: ViewContainerRef,
              private roleService: RoleService) {
    this.sub = this.roleService.roles$.subscribe(() => this.update());
  }

  @Input()
  set hasRole(value: RoleName | RoleName[]) {
    this.roles = value == null ? [] : (Array.isArray(value) ? value : [value]);
    this.update();
  }

  /** 'any' (по умолчанию) — хотя бы одна роль, 'every' — все роли */
  @Input()
  set hasRoleMode(value: 'any' | 'every') {
    this.mode = value === 'every' ? 'every' : 'any';
    this.update();
  }

  /** Шаблон, который показывается, если роли нет */
  @Input()
  set hasRoleElse(template: TemplateRef<any> | null) {
    this.elseTemplate = template || null;
    this.update();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private update() {
    const allowed = this.mode === 'every'
      ? this.roleService.hasEveryRole(this.roles)
      : this.roleService.hasAnyRole(this.roles);

    const template = allowed ? this.templateRef : this.elseTemplate;

    if (template === this.renderedTemplate) {
      return;
    }

    this.viewContainer.clear();
    if (template) {
      this.viewContainer.createEmbeddedView(template);
    }
    this.renderedTemplate = template;
  }
}

/**
 * Обратная проверка — показывает элемент, если у пользователя НЕТ такой роли.
 *
 *  <button *hasNotRole="'MioReadOnlyUser'">Редактировать</button>
 *  <button *hasNotRole="['MioReadOnlyUser', 'Techsupervision_hgf']">Редактировать</button>
 */
@Directive({
  selector: '[hasNotRole]',
  standalone: true
})
export class HasNotRoleDirective implements OnDestroy {

  private roles: RoleName[] = [];
  private hasView = false;
  private sub: Subscription;

  constructor(private templateRef: TemplateRef<any>,
              private viewContainer: ViewContainerRef,
              private roleService: RoleService) {
    this.sub = this.roleService.roles$.subscribe(() => this.update());
  }

  @Input()
  set hasNotRole(value: RoleName | RoleName[]) {
    this.roles = value == null ? [] : (Array.isArray(value) ? value : [value]);
    this.update();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private update() {
    const allowed = this.roleService.hasNoneOfRoles(this.roles);

    if (allowed && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!allowed && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
